/**
 * i18n System - Lightweight Internationalization
 * Zero dependencies, full TypeScript support ready
 */

import enTranslations from './translations/en.json';
import itTranslations from './translations/it.json';
import esTranslations from './translations/es.json';

// Available languages
export const LANGUAGES = {
  en: { code: 'en', name: 'English', flag: '🇬🇧' },
  it: { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  es: { code: 'es', name: 'Español', flag: '🇪🇸' },
};

// All translations
const translations = {
  en: enTranslations,
  it: itTranslations,
  es: esTranslations,
};

/**
 * Get nested translation by key path
 * @param {Object} obj - Translation object
 * @param {string} path - Dot-notation path (e.g., "auth.loginTitle")
 * @returns {string|undefined} Translation string
 */
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

/**
 * Get translation for a given key
 * @param {string} key - Translation key in dot notation
 * @param {string} language - Language code
 * @param {Object} variables - Variables for interpolation
 * @returns {string} Translated string or key if not found
 */
export const translate = (key, language = 'en', variables = {}) => {
  const translation = getNestedValue(translations[language], key);

  if (!translation) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`Translation missing: ${key} (${language})`);
    }
    return key;
  }

  // Simple variable interpolation: {{variable}}
  return translation.replace(/\{\{(\w+)\}\}/g, (match, varName) => {
    return variables[varName] !== undefined ? variables[varName] : match;
  });
};

/**
 * Get browser's preferred language
 * @returns {string} Language code
 */
export const getBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage;
  const langCode = browserLang.split('-')[0]; // 'en-US' -> 'en'

  // Return if supported, otherwise default to 'en'
  return LANGUAGES[langCode] ? langCode : 'en';
};

/**
 * Detect if language is RTL (Right-to-Left)
 * @param {string} language - Language code
 * @returns {boolean}
 */
export const isRTL = (language) => {
  const rtlLanguages = ['ar', 'he', 'fa', 'ur'];
  return rtlLanguages.includes(language);
};

/**
 * Get all available language codes
 * @returns {string[]}
 */
export const getAvailableLanguages = () => {
  return Object.keys(LANGUAGES);
};

/**
 * Get language name
 * @param {string} code - Language code
 * @returns {string} Language name
 */
export const getLanguageName = (code) => {
  return LANGUAGES[code]?.name || code;
};

/**
 * Format number according to locale
 * @param {number} number - Number to format
 * @param {string} language - Language code
 * @returns {string}
 */
export const formatNumber = (number, language = 'en') => {
  return new Intl.NumberFormat(language).format(number);
};

/**
 * Format date according to locale
 * @param {Date|string} date - Date to format
 * @param {string} language - Language code
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string}
 */
export const formatDate = (date, language = 'en', options = {}) => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const defaultOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options,
  };
  return new Intl.DateTimeFormat(language, defaultOptions).format(dateObj);
};

/**
 * Format currency according to locale
 * @param {number} amount - Amount to format
 * @param {string} language - Language code
 * @param {string} currency - Currency code (USD, EUR, etc.)
 * @returns {string}
 */
export const formatCurrency = (amount, language = 'en', currency = 'USD') => {
  return new Intl.NumberFormat(language, {
    style: 'currency',
    currency,
  }).format(amount);
};

/**
 * Pluralization helper
 * @param {number} count - Count for pluralization
 * @param {Object} forms - Plural forms { zero, one, many }
 * @returns {string}
 */
export const pluralize = (count, forms) => {
  if (count === 0 && forms.zero) return forms.zero;
  if (count === 1) return forms.one;
  return forms.many;
};

export default {
  translate,
  getBrowserLanguage,
  isRTL,
  getAvailableLanguages,
  getLanguageName,
  formatNumber,
  formatDate,
  formatCurrency,
  pluralize,
  LANGUAGES,
};
